import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from './stores/auth.store';
import { api } from './lib/api';
import { AppShell } from './components/layout/AppShell';
import { FrozenGate } from './components/layout/FrozenGate';
import { LoginCurtainRoot } from './components/ui/LoginCurtain';
import { LoginPage } from './features/auth/LoginPage';
// La home. Sta in features/oggi perché è la pagina "Oggi": un riepilogo di
// soldi (liquidità, mesi, periodo scelto, lavoro, fatture, da pagare) con lo
// stream delle cose da fare in fondo.
import { OggiPage } from './features/oggi/OggiPage';
import { ContactsPage } from './features/contacts/ContactsPage';
import { ContactDetailPage } from './features/contacts/ContactDetailPage';
import { ClientDetailPage } from './features/crm/ClientDetailPage';
import { DivisionPage } from './features/projects/DivisionPage';
import { ProjectsPage } from './features/projects/ProjectsPage';
import { ProjectDetailPage } from './features/projects/ProjectDetailPage';
import { TalentPage } from './features/talent/TalentPage';
import { CourseDetailPage } from './features/talent/CourseDetailPage';
import { CandidateDetailPage } from './features/talent/CandidateDetailPage';
import { BodyRentalDetailPage } from './features/talent/BodyRentalDetailPage';
import { DigitalDesignPage } from './features/design/DigitalDesignPage';
import { SuppliersPage } from './features/suppliers/SuppliersPage';
import { SupplierDetailPage } from './features/suppliers/SupplierDetailPage';
import { InvoicesPage } from './features/invoices/InvoicesPage';
import { InvoiceDetailPage } from './features/invoices/InvoiceDetailPage';
import { QuoteDetailPage } from './features/quotes/QuoteDetailPage';
import { QuoteSignPage } from './features/quotes/QuoteSignPage';
import { ServicesPage } from './features/services/ServicesPage';
import { ServiceDetailPage } from './features/services/ServiceDetailPage';
import { VaultPage } from './features/vault/VaultPage';
import { ReportPage } from './features/reports/ReportPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { TasksPage } from './features/tasks/TasksPage';
import { TaskDetailPage } from './features/tasks/TaskDetailPage';
import { DealsPage } from './features/deals/DealsPage';
import { DealDetailPage } from './features/deals/DealDetailPage';
import { PipelinesPage } from './features/deals/PipelinesPage';
import { AdminPage } from './features/admin/AdminPage';
import { Division } from '@masamune/shared';

export function App() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    if (accessToken && !user) {
      api.get<{ user: any }>('/auth/me')
        .then((res) => useAuthStore.getState().setAuth(res.user, accessToken))
        .catch(() => useAuthStore.getState().clear());
    }
  }, [accessToken, user]);

  return (
    <>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Public quote signing — fuori da auth shell */}
      <Route path="/sign/quotes/:token" element={<QuoteSignPage />} />
      <Route element={<AppShell />}>
        {/* FASE 0: FrozenGate mostra la pagina di pausa sulle route dei moduli
            in FROZEN_MODULES (lib/frozen.ts); le altre passano intatte. */}
        <Route element={<FrozenGate />}>
          <Route path="/" element={<OggiPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tasks/:id" element={<TaskDetailPage />} />
          <Route path="/trattative" element={<DealsPage />} />
          {/* Prima del dettaglio: "pipeline" è un segmento fisso e deve vincere
              sull'id, altrimenti finirebbe a cercare una trattativa che non c'è */}
          <Route path="/trattative/pipeline" element={<PipelinesPage />} />
          <Route path="/trattative/:id" element={<DealDetailPage />} />
          {/* La rubrica unica: clienti, potenziali e professionisti insieme.
              Resta su /crm perché è la stessa sezione, cambiata dentro: i link
              e i segnalibri vecchi continuano a funzionare. */}
          <Route path="/crm" element={<ContactsPage />} />
          <Route path="/contatti/:kind/:id" element={<ContactDetailPage />} />
          {/* La scheda cliente estesa (diario, documenti, marginalità) resta
              dov'era: ci arrivano i link dalle altre sezioni e il pulsante
              "Scheda estesa" della scheda contatto. */}
          <Route path="/clients/:id" element={<ClientDetailPage />} />
          {/* Una sezione sola al posto delle tre divisioni. Le vecchie rotte
              restano vive come scorciatoie: rendono la stessa pagina già
              filtrata, così i link salvati non si rompono. */}
          <Route path="/progetti" element={<ProjectsPage />} />
          <Route path="/divisions/marketing" element={<DivisionPage division={Division.MKT} />} />
          <Route path="/divisions/product" element={<DivisionPage division={Division.PROD} />} />
          <Route path="/divisions/design" element={<DigitalDesignPage />} />
          <Route path="/projects/:id" element={<ProjectDetailPage />} />
          <Route path="/talent" element={<TalentPage />} />
          <Route path="/talent/body-rental/:id" element={<BodyRentalDetailPage />} />
          <Route path="/courses/:id" element={<CourseDetailPage />} />
          <Route path="/candidates/:id" element={<CandidateDetailPage />} />
          <Route path="/suppliers" element={<SuppliersPage />} />
          <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
          <Route path="/admin" element={<AdminPage />} />
          {/* Contabilità: fuori dal menu (i soldi stanno in Amministrazione, tab
              Documenti). Le route restano vive per i vecchi link e per i
              dettagli, senza FrozenGate: FATTURAZIONE non è un modulo in pausa. */}
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
          <Route path="/quotes" element={<Navigate to="/admin?tab=documenti&type=quote" replace />} />
          <Route path="/quotes/:id" element={<QuoteDetailPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:id" element={<ServiceDetailPage />} />
          <Route path="/vault" element={<VaultPage />} />
          <Route path="/report" element={<ReportPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Routes>
    {/* Sipario di uscita dal login: fuori dalle route, sopravvive a navigate('/') */}
    <LoginCurtainRoot />
    </>
  );
}
